import React, { useState, useEffect } from 'react';
import {
  AddressBook, ArrowsLeftRight, Bank, Buildings, CalendarCheck, CreditCard, Door, FloppyDisk, Money, Note, User,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { leereIdZuNull } from '../../../lib/forms/sanitize';
import KontaktKanaeleEditor from '../../../components/kontakt/KontaktKanaeleEditor';
import { type KontaktKanal, speichereKanaele } from '../../../lib/kontakt/kontaktKanaele';
import {
  ladeAktiveMieterDerEinheit, planeMieterWechsel, fuehreWechselAus, legeMieterOrdnerAn,
  heuteIso, mieterAnzeigeName, type VormieterInfo,
} from '../../../lib/wechsel/api';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormSegment } from '../../../components/island/form/FormSegment';
import { FormAuswahl } from '../../../components/island/form/FormAuswahl';
import { FormSuche } from '../../../components/island/form/FormSuche';
import { FormOptionen } from '../../../components/island/form/FormOptionen';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { fehlerMeldung } from '../../../components/island/form/form-logik';
import { ZEILEN_ICON } from '../../../components/island/form/form-stil';

interface Objekt {
  id: string;
  bezeichnung: string;
  strasse: string;
  hausnummer: string;
}

const ANREDEN = ['Herr', 'Frau', 'Divers', 'Sonstige'] as const;
// Optionen wie bisher im <select>; Icons je Zahlungsweise für die Liste (Handoff §4).
const ZAHLUNGSWEISEN = [
  { wert: 'Lastschrift', icon: Bank },
  { wert: 'Überweisung', icon: ArrowsLeftRight },
  { wert: 'Bar', icon: Money },
  { wert: 'Dauerauftrag', icon: CalendarCheck },
] as const;
// Werte klein wie bisher; angezeigt wurden sie per CSS „capitalize“ – hier dieselbe Schreibweise als Text.
const STATUS = [
  { wert: 'aktiv', label: 'Aktiv' },
  { wert: 'gekündigt', label: 'Gekündigt' },
  { wert: 'interessent', label: 'Interessent' },
  { wert: 'ehemalig', label: 'Ehemalig' },
] as const;

export default function MieterErstellenFormular({ onDone }: { onDone: () => void }) {
  const [loading, setLoading] = useState(false);
  const [objekte, setObjekte] = useState<Objekt[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [kanaele, setKanaele] = useState<KontaktKanal[]>([]);
  const fehler = useFormFehler();

  const [formData, setFormData] = useState({
    anrede: 'Herr',
    vorname: '',
    nachname: '',
    telefon: '',
    email: '',
    geburtsdatum: '',
    mietbeginn: '',
    mietende: '',
    kaltmiete: 0,
    nebenkosten_vorauszahlung: 0,
    kaution: 0,
    zahlungsweise: 'Lastschrift',
    bankverbindung: '',
    status: 'aktiv',
    notizen: '',
    objekt_id: '',
    einheit: '',
    einheit_id: '',
  });
  const [einheiten, setEinheiten] = useState<{ id: string; bezeichnung: string }[]>([]);
  const [vormieter, setVormieter] = useState<VormieterInfo[]>([]);
  const [wechselModus, setWechselModus] = useState<'wechsel' | 'mitmieter'>('wechsel');

  useEffect(() => {
    fetchObjekte();
  }, []);

  useEffect(() => {
    let aktiv = true;
    if (!formData.objekt_id) { setEinheiten([]); return; }
    supabase.from('objekt_einheiten').select('id, bezeichnung')
      .eq('objekt_id', formData.objekt_id).order('bezeichnung')
      .then(({ data }) => { if (aktiv) setEinheiten((data || []) as { id: string; bezeichnung: string }[]); });
    return () => { aktiv = false; };
  }, [formData.objekt_id]);

  useEffect(() => {
    let aktiv = true;
    if (!formData.einheit_id) { setVormieter([]); return; }
    ladeAktiveMieterDerEinheit(formData.einheit_id).then((v) => { if (aktiv) { setVormieter(v); setWechselModus('wechsel'); } });
    return () => { aktiv = false; };
  }, [formData.einheit_id]);

  const fetchObjekte = async () => {
    try {
      const { data, error } = await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer');
      if (error) throw error;
      setObjekte(data || []);
    } catch (err) {
      console.error('Error fetching objects:', err);
    }
  };

  const filteredObjekte = objekte.filter(o =>
    o.bezeichnung.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.strasse.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectObjekt = (obj: Objekt) => {
    setFormData(prev => ({ ...prev, objekt_id: obj.id, einheit: '', einheit_id: '' }));
    setSearchQuery(obj.bezeichnung);
    setIsDropdownOpen(false);
  };

  const setzeObjektSuche = (wert: string) => {
    setSearchQuery(wert);
    setIsDropdownOpen(true);
    if (formData.objekt_id) setFormData(p => ({ ...p, objekt_id: '' }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
    fehler.korrigiert(name);
  };

  const setzeFeld = (name: 'anrede' | 'zahlungsweise' | 'status', value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    fehler.leeren();
    try {
      const { data: neu, error } = await supabase
        .from('mieter')
        .insert([{
          ...formData,
          // Leerstring aus den Auswahlfeldern wird zu NULL — uuid-Spalten lehnen '' ab.
          objekt_id: leereIdZuNull(formData.objekt_id),
          einheit_id: leereIdZuNull(formData.einheit_id),
        }])
        .select('id')
        .single();

      if (error) throw error;

      if (neu?.id) {
        await speichereKanaele(supabase, 'mieter', neu.id, kanaele);

        // Drive-Ordner fuer den neuen Mieter (best-effort)
        try { await legeMieterOrdnerAn(neu.id); } catch (e) { console.error('Mieter-Ordner anlegen fehlgeschlagen:', e); }
        // Vormieter-Wechsel planen (+ sofort ausfuehren, wenn faellig)
        if (wechselModus === 'wechsel' && vormieter.length > 0 && formData.einheit_id) {
          try {
            const faellig = formData.mietbeginn || heuteIso();
            const { data: { user } } = await supabase.auth.getUser();
            const ids = await planeMieterWechsel({
              objektId: formData.objekt_id, einheitId: formData.einheit_id,
              altRefIds: vormieter.map((v) => v.id), neuRefId: neu.id,
              faelligAm: faellig, angelegtVon: user?.id ?? null,
            });
            if (faellig <= heuteIso()) {
              fuehreWechselAus(ids).catch((e) => console.error('Wechsel-Ausfuehrung fehlgeschlagen (Cron holt nach):', e));
            }
          } catch (e) { console.error('Wechsel planen fehlgeschlagen:', e); }
        }
      }

      onDone();
    } catch (err: unknown) {
      console.error('Error creating tenant:', err);
      fehler.zeige('Fehler beim Speichern: ' + fehlerMeldung(err, undefined));
    } finally {
      setLoading(false);
    }
  };

  const einheitHinweis = formData.objekt_id && !formData.einheit_id ? 'Ohne Einheit keine automatische Wechsel-Erkennung.' : undefined;
  const namen = vormieter.map(mieterAnzeigeName);

  return (
    // Browser-Prüfung bleibt wie bisher (Pflichtnamen, E-Mail- und Zahlenfelder); ungültige Felder werden rot.
    <FormRahmen onSubmit={handleSubmit} fehler={fehler.text} browserPruefung onUngueltig={fehler.markiere}
      knopfleiste={
        <FormKnopfleiste abbrechen={{ label: 'Abbrechen', onClick: onDone }}
          haupt={{ label: 'Mieter speichern', icon: FloppyDisk, laedt: loading }} />
      }>
      <FormAbschnitt titel="Persönliche Stammdaten" icon={User} farbe="gruen" index={0}
        pflicht={2} fehlt={['vorname', 'nachname'].filter(f => fehler.istFehler(f)).length}>
        <FormSegment label="Anrede" optionen={ANREDEN.map(a => ({ wert: a, label: a }))} wert={formData.anrede}
          onChange={(v) => setzeFeld('anrede', v)} />
        <FormZeile>
          <FormFeld label="Vorname" name="vorname" value={formData.vorname} onChange={handleChange} placeholder="Max"
            pflicht required fehler={fehler.istFehler('vorname')} />
          <FormFeld label="Nachname" name="nachname" value={formData.nachname} onChange={handleChange} placeholder="Mustermann"
            pflicht required fehler={fehler.istFehler('nachname')} />
        </FormZeile>
        <FormFeld label="Geburtsdatum" name="geburtsdatum" typ="date" value={formData.geburtsdatum} onChange={handleChange} />
      </FormAbschnitt>

      <FormAbschnitt titel="Kontaktwege" icon={AddressBook} farbe="blau" index={1}
        tip="Kontaktwege – Mehrere E-Mails und Nummern möglich, der Stern markiert die Hauptadresse">
        <KontaktKanaeleEditor kanaele={kanaele} onChange={setKanaele} />
      </FormAbschnitt>

      <FormAbschnitt titel="Zuweisung Mieteinheit" icon={Buildings} farbe="gold" index={2}
        tip="Zuweisung Mieteinheit – Mit Einheit erkennt das System einen Mieterwechsel automatisch">
        <FormZeile spalten="minmax(0,2fr) minmax(0,1fr)">
          <FormSuche label="Objekt suchen" platzhalter="Objektname oder Straße..." tip="Objekt suchen – Objekt, in dem der Mieter wohnt"
            wert={searchQuery} onChange={(e) => setzeObjektSuche(e.target.value)}
            offen={isDropdownOpen} mindestLaenge={0} leerText="Kein Objekt gefunden"
            onFocus={() => setIsDropdownOpen(true)} onBlur={() => setIsDropdownOpen(false)} onSchliessen={() => setIsDropdownOpen(false)}
            ausgewaehlt={!!formData.objekt_id} onEntfernen={() => setzeObjektSuche('')}
            treffer={filteredObjekte.map(o => ({
              id: o.id, label: o.bezeichnung, unter: `${o.strasse} ${o.hausnummer}`, icon: Buildings,
              iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe,
            }))}
            onWaehlen={(id) => { const o = objekte.find(x => x.id === id); if (o) handleSelectObjekt(o); }} />
          {einheiten.length > 0 ? (
            <FormAuswahl label="Einheit / Wohnung" hinweis={einheitHinweis}
              optionen={[{ wert: '', label: '— Einheit wählen —' }, ...einheiten.map(e2 => ({ wert: e2.id, label: e2.bezeichnung, icon: Door }))]}
              wert={formData.einheit_id}
              onChange={(id) => {
                const einheit = einheiten.find((x) => x.id === id);
                setFormData(prev => ({ ...prev, einheit_id: id, einheit: einheit?.bezeichnung || '' }));
              }} />
          ) : (
            <FormFeld label="Einheit / Wohnung" name="einheit" value={formData.einheit} onChange={handleChange}
              placeholder="z.B. Whg 04" hinweis={einheitHinweis} />
          )}
        </FormZeile>
        {vormieter.length > 0 && (
          <FormOptionen beschriftung="Mieterwechsel oder Mitmieter" wert={wechselModus} onChange={setWechselModus}
            kopf={<>Auf dieser Einheit aktiv: {namen.join(', ')}</>}
            optionen={[
              { wert: 'wechsel', titel: 'Mieterwechsel', text: <>{namen.join(' und ')} {vormieter.length > 1 ? 'werden' : 'wird'} zum {formData.mietbeginn || 'Mietbeginn'} zu Ehemaligen (Ordner wandert nach „Ehemalige Mieter").</> },
              { wert: 'mitmieter', titel: 'Mitmieter', text: 'zieht zusätzlich ein, niemand zieht aus.' },
            ]} />
        )}
      </FormAbschnitt>

      <FormAbschnitt titel="Vertrags- & Finanzdetails" icon={CreditCard} farbe="orange" index={3}
        tip="Vertrags- & Finanzdetails – Beträge in Euro, Status steuert die Mieterliste">
        <FormZeile>
          <FormFeld label="Mietbeginn" name="mietbeginn" typ="date" value={formData.mietbeginn} onChange={handleChange} fehler={fehler.istFehler('mietbeginn')} />
          <FormFeld label="Mietende" name="mietende" typ="date" value={formData.mietende} onChange={handleChange} fehler={fehler.istFehler('mietende')} />
        </FormZeile>
        <FormAuswahl label="Zahlungsweise" optionen={ZAHLUNGSWEISEN.map(z => ({ wert: z.wert, label: z.wert, icon: z.icon }))}
          wert={formData.zahlungsweise} onChange={(v) => setzeFeld('zahlungsweise', v)} />
        <FormZeile>
          <FormFeld label="Kaltmiete (€)" name="kaltmiete" typ="number" value={formData.kaltmiete} onChange={handleChange} fehler={fehler.istFehler('kaltmiete')} />
          <FormFeld label="Nebenkosten (€)" name="nebenkosten_vorauszahlung" typ="number" value={formData.nebenkosten_vorauszahlung} onChange={handleChange} fehler={fehler.istFehler('nebenkosten_vorauszahlung')} />
          <FormFeld label="Kaution (€)" name="kaution" typ="number" value={formData.kaution} onChange={handleChange} fehler={fehler.istFehler('kaution')} />
        </FormZeile>
        <FormSegment label="Status" optionen={STATUS.map(s => ({ wert: s.wert, label: s.label }))} wert={formData.status}
          onChange={(v) => setzeFeld('status', v)} />
        <FormFeld label="Bankverbindung (IBAN / Name)" name="bankverbindung" value={formData.bankverbindung} onChange={handleChange}
          placeholder="DE89 1234 ..." icon={Bank} />
      </FormAbschnitt>

      <FormAbschnitt titel="Zusätzliche Informationen" icon={Note} farbe="violett" index={4}
        tip="Zusätzliche Informationen – Besondere Vereinbarungen, nur intern sichtbar">
        <FormFeld label="Notizen & Bemerkungen" name="notizen" value={formData.notizen} onChange={handleChange} zeilen={3}
          placeholder="Besondere Vereinbarungen..." />
      </FormAbschnitt>
    </FormRahmen>
  );
}
